// src/components/resources/quizzes/components/QuestionDescription.tsx

"use client"

import { motion } from 'motion/react'
import { cn } from '~/lib/utils'
import RiTableLine from '~/icons/remix/generated/RiTableLine'
import type { QuizQuestion } from '~/lib/resources/types/tools/quiz'

interface QuestionDescriptionProps {
  question: QuizQuestion
  className?: string
}

// Define interfaces for structured content with strict types
interface TableData {
  headers: string[]
  rows: string[][]
  alignments?: Array<'left' | 'center' | 'right'>
  caption?: string
  footer?: string[]
  summary?: string
  error?: {
    type: 'warning' | 'error'
    message: string
  }
}

interface ListData {
  items: string[]
  ordered?: boolean
}

interface KeyValueData {
  key: string
  value: string
}

interface SectionData {
  title: string
  content: string
  level?: 1 | 2 | 3
  items?: Block[]
}

// Update Block type to include new formats
type BlockContent = {
  text: string
  h1: string
  h2: string
  h3: string
  table: TableData
  code: CodeData
  section: SectionData
  definition: { term: string; definition: string; isInline?: boolean }
  metric: { label: string; value: string; unit?: string; trend?: 'up' | 'down' | 'neutral' }
  callout: { text: string; variant?: 'info' | 'warning' | 'error' | 'success' }
  listItem: { number: number; content: string }
  list: ListData
  keyValue: KeyValueData[]
  note: string
}

interface Block {
  type: keyof BlockContent
  content: BlockContent[keyof BlockContent]
  items?: Block[]
  level?: 1 | 2 | 3
  language?: string
}

interface CodeData {
  content: string
  language?: string
}

// Update the component rendering logic with proper typing
export function QuestionDescription({ question, className }: QuestionDescriptionProps): JSX.Element | null {
  // Only show description if explicitly enabled
  if (!question.description || question.showDescription === false) {
    return null
  }

  const blocks = parseContent(question.description)
  
  if (!blocks.length) return null

  /**
   * Renders a single block of content based on its type
   * @param block - The block to render
   * @param index - The index or key for React rendering
   * @returns JSX element for the block
   */
  const renderBlock = (block: Block, index: number | string): JSX.Element => {
    const headerConfig = {
      h1: { class: 'text-xl font-bold', hasBorder: true },
      h2: { class: 'text-base font-semibold', hasBorder: true },
      h3: { class: 'text-sm font-medium', hasBorder: false }
    } as const

    switch (block.type) {
      case 'h1':
      case 'h2':
      case 'h3': {
        const config = headerConfig[block.type]
        const Tag = block.type
        return (
          <div key={index} className={config.hasBorder ? 'border-b border-gray-200 dark:border-gray-700' : ''}>
            <Tag 
              className={`${config.class} text-gray-900 dark:text-white mb-2`}
              dangerouslySetInnerHTML={{ __html: parseInlineFormatting(block.content as string) }}
            />
            {block.items && block.items.length > 0 && (
              <div className="space-y-2 mb-3">
                {block.items.map((item, i) => renderBlock(item, `${index}-${i}`))}
              </div>
            )}
          </div>
        )
      }

      case 'definition': {
        const { term, definition } = block.content as BlockContent['definition']
        return (
          <div key={index} className="flex gap-2 items-baseline">
            <span className="font-medium text-gray-900 dark:text-white">{term}:</span>
            <span className="text-gray-600 dark:text-gray-400">{definition}</span>
          </div>
        )
      }

      case 'metric': {
        const { label, value, unit, trend } = block.content as BlockContent['metric']
        return (
          <div key={index} className="flex gap-2 items-baseline">
            <span className="text-gray-500 dark:text-gray-400">[{label}]</span>
            <span className="font-mono text-gray-900 dark:text-white">
              {value}
              {unit && <span className="ml-1 text-gray-500">{unit}</span>}
              {trend && (
                <span className={`ml-2 ${
                  trend === 'up' ? 'text-green-500' :
                  trend === 'down' ? 'text-red-500' :
                  'text-gray-500'
                }`}>
                  {trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→'}
                </span>
              )}
            </span>
          </div>
        )
      }

      case 'callout': {
        const { text, variant } = block.content as BlockContent['callout']
        return (
          <div
            key={index}
            className={`p-3 rounded-md ${
              variant === 'info'
                ? 'bg-blue-50 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
                : variant === 'warning'
                ? 'bg-yellow-50 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300'
                : variant === 'error'
                ? 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-300'
                : variant === 'success'
                ? 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                : 'bg-gray-50 text-gray-800 dark:bg-gray-800/50 dark:text-gray-300'
            }`}
          >
            <p className="text-sm" dangerouslySetInnerHTML={{ __html: parseInlineFormatting(text) }} />
          </div>
        )
      }

      case 'listItem': {
        const { number, content } = block.content as BlockContent['listItem']
        return (
          <div key={index} className="flex gap-2">
            <span className="font-medium text-gray-900 dark:text-white">{number}.</span>
            <span 
              className="text-gray-600 dark:text-gray-400"
              dangerouslySetInnerHTML={{ __html: parseInlineFormatting(content) }}
            />
          </div>
        )
      }

      case 'list': {
        const { items, ordered } = block.content as BlockContent['list']
        return (
          <div key={index} className="space-y-1">
            {items.map((item, i) => (
              <div key={i} className="flex gap-2">
                <span className="font-medium text-gray-900 dark:text-white">
                  {ordered ? `${i + 1}.` : '•'}
                </span>
                <span 
                  className="text-gray-600 dark:text-gray-400"
                  dangerouslySetInnerHTML={{ __html: parseInlineFormatting(item) }}
                />
              </div>
            ))}
          </div>
        )
      }

      case 'keyValue': {
        const items = block.content as BlockContent['keyValue']
        return (
          <div key={index} className="space-y-1">
            {items.map((item, i) => (
              <div key={i} className="flex gap-2 items-baseline">
                <span className="font-medium text-gray-900 dark:text-white">{item.key}:</span>
                <span className="text-gray-600 dark:text-gray-400">{item.value}</span>
              </div>
            ))}
          </div>
        )
      }

      case 'note': {
        return (
          <div key={index} className="text-sm text-gray-500 dark:text-gray-400 italic">
            {block.content as string}
          </div>
        )
      }

      case 'code': {
        const { content, language } = block.content as BlockContent['code']
        return (
          <pre key={index} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-md overflow-x-auto">
            <code className={`language-${language ?? 'plaintext'}`}>
              {content}
            </code>
          </pre>
        )
      }

      case 'section': {
        const sectionContent = block.content as SectionData
        const { title, content, level = 2, items = [] } = sectionContent
        const Tag = `h${level}` as keyof JSX.IntrinsicElements
        return (
          <div key={index} className="space-y-2">
            <Tag className={cn(
              "font-semibold text-gray-900 dark:text-white",
              level === 1 ? "text-lg" :
              level === 2 ? "text-base" :
              "text-sm"
            )}>
              {title}
            </Tag>
            <div className="text-gray-600 dark:text-gray-400">
              {content}
            </div>
            {items.length > 0 && (
              <div className="space-y-2 ml-4">
                {items.map((item, i) => renderBlock(item, `${index}-${i}`))}
              </div>
            )}
          </div>
        )
      }

      case 'table': {
        const { headers, rows, alignments = [], caption, footer } = block.content as BlockContent['table']
        return (
          <div key={index} className="relative">
            <div className="absolute -left-2 top-0 flex h-5 w-5 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900/50">
              <RiTableLine className="h-3 w-3 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div className="ml-4">
              {caption && (
                <div className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  {caption}
                </div>
              )}
              <table className="w-full divide-y divide-gray-200 rounded-lg bg-white text-sm dark:divide-gray-700 dark:bg-gray-800" role="grid">
                <thead>
                  <tr role="row">
                    {headers.map((header, i) => (
                      <th
                        key={i}
                        role="columnheader"
                        scope="col"
                        className={cn(
                          "whitespace-nowrap bg-gray-50 px-2 py-1.5 font-medium text-gray-900 dark:bg-gray-800/50 dark:text-white",
                          {
                            'text-left': !alignments[i] || alignments[i] === 'left',
                            'text-center': alignments[i] === 'center',
                            'text-right': alignments[i] === 'right'
                          }
                        )}
                        dangerouslySetInnerHTML={{ __html: parseInlineFormatting(header) }}
                      />
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {rows.map((row, i) => (
                    <tr 
                      key={i}
                      role="row"
                      className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50"
                    >
                      {row.map((cell, j) => {
                        const dashRegex = /^[-—]+$/
                        const isDashed = dashRegex.exec(cell) !== null
                        return (
                          <td
                            key={j}
                            role="cell"
                            className={cn(
                              "whitespace-pre-line px-2 py-1",
                              {
                                'text-left': !alignments[j] || alignments[j] === 'left',
                                'text-center': alignments[j] === 'center',
                                'text-right': alignments[j] === 'right',
                                'text-gray-400 dark:text-gray-500': isDashed,
                                'text-gray-600 dark:text-gray-300': !isDashed
                              }
                            )}
                            dangerouslySetInnerHTML={isDashed ? { __html: '—' } : { __html: parseInlineFormatting(cell) }}
                          />
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
                {footer && footer.length > 0 && (
                  <tfoot className="border-t border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
                    {footer.map((footerText, i) => (
                      <tr key={i}>
                        <td 
                          colSpan={headers.length}
                          className="px-2 py-1 text-sm text-gray-500 dark:text-gray-400"
                          dangerouslySetInnerHTML={{ __html: parseInlineFormatting(footerText) }}
                        />
                      </tr>
                    ))}
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        )
      }

      case 'text':
      default:
        return (
          <p 
            key={index} 
            className="text-gray-600 dark:text-gray-400"
            dangerouslySetInnerHTML={{ __html: parseInlineFormatting(block.content as string) }}
          />
        )
    }
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={cn(
        "space-y-4 rounded-2xl bg-gradient-to-br from-white to-gray-50 p-4 shadow-sm ring-1 ring-gray-900/5",
        "dark:from-gray-900 dark:to-gray-800/50 dark:ring-white/5",
        className
      )}
    >
      {blocks.map((block, index) => (
        <div key={index}>
          {renderBlock(block, index)}
        </div>
      ))}
    </motion.div>
  )
}

/**
 * Parses inline formatting in text content (bold, italic, code, links)
 * @param text - The text to parse for inline formatting
 * @returns HTML string with inline formatting applied
 */
/** Escape a string for safe interpolation into HTML text or a double-quoted attribute. */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * Allow only safe link schemes. Rejects `javascript:`, `data:`, `vbscript:`,
 * protocol-relative `//host` (open-redirect/phishing), etc. — a bare `#` is
 * returned for anything that isn't http(s)/mailto/tel or a genuine
 * root/relative path, so a malicious URL can never reach the `href`.
 *
 * @internal Exported for unit testing of the sanitizer.
 */
export function safeLinkHref(rawUrl: string): string {
  // Normalize BEFORE the scheme/allowlist checks: browsers ignore C0 control
  // chars + DEL and canonicalize backslashes to forward slashes when resolving a
  // URL. Without this, `java\nscript:alert(1)` (embedded newline) or
  // `[x](/\evil.com)` / `[x](\\evil.com)` (backslashes -> `//evil.com`) would
  // slip past the allowlist and resolve to a `javascript:` / protocol-relative URL.
  const url = rawUrl
    // eslint-disable-next-line no-control-regex -- deliberately stripping C0 controls + DEL
    .replace(/[\u0000-\u001F\u007F]/g, '')
    .replace(/\\/g, '/')
    .trim()
  // Protocol-relative (`//host`) resolves to an external origin — reject it
  // before the root-relative (`/…`) allowance below would let it through.
  if (url.startsWith('//')) return '#'
  if (/^(https?:|mailto:|tel:)/i.test(url)) return url
  if (/^(\/|\.\/|\.\.\/|#|\?)/.test(url)) return url
  // Anything with a scheme we don't recognise (incl. javascript:) is unsafe.
  if (/^[a-z][a-z0-9+.-]*:/i.test(url)) return '#'
  // Schemeless, non-path text (e.g. "example.com") — treat as relative.
  return url
}

/**
 * Render a limited inline-markdown subset (`**bold**`, `*em*`, `` `code` ``,
 * `[label](url)`) to an HTML string for `dangerouslySetInnerHTML`.
 *
 * SECURITY: the ENTIRE input is HTML-escaped first, so any raw HTML — whether in
 * a link label, an emphasis body, or plain text — is inert; the passes below
 * only insert known-safe tags around already-escaped content. Link hrefs are
 * additionally scheme-checked via {@link safeLinkHref}.
 *
 * @internal Exported for unit testing.
 */
export function parseInlineFormatting(text: string): string {
  let out = escapeHtml(text)

  // **bold**
  out = out.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
  // *italic*
  out = out.replace(/\*(.*?)\*/g, '<em>$1</em>')
  // `code`
  out = out.replace(/`(.*?)`/g, '<code>$1</code>')
  // [label](url) — `label`/`url` are already HTML-escaped by the pass above, so
  // the URL is used directly (re-escaping would double-encode `&amp;`).
  out = out.replace(/\[(.*?)\]\((.*?)\)/g, (_match, label: string, url: string) => {
    const href = safeLinkHref(url)
    return `<a href="${href}" class="text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300">${label}</a>`
  })

  return out
}

/**
 * Parses content string into structured blocks for rendering
 * @param content - The raw content string to parse
 * @returns Array of Block objects representing the parsed content
 */
function parseContent(content: string): Block[] {
  const lines = content.split('\n')
  const blocks: Block[] = []
  let currentTable: TableData | null = null

  for (const line of lines) {
    const trimmedLine = line.trim()
    if (!trimmedLine) {
      currentTable = null
      continue
    }

    // Parse table rows
    if (trimmedLine.includes('|')) {
      const cells = trimmedLine
        .split('|')
        .map(cell => cell.trim())
        .filter(Boolean)
      
      // Skip separator rows that are all dashes - use RegExp.exec() instead of String.match()
      const dashSeparatorRegex = /^[-|]+$/
      if (cells.every(cell => dashSeparatorRegex.exec(cell) !== null)) {
        continue
      }
      
      if (!currentTable) {
        currentTable = {
          headers: cells,
          rows: []
        }
        blocks.push({
          type: 'table',
          content: currentTable
        })
      } else {
        currentTable.rows.push(cells)
      }
      continue
    } else {
      currentTable = null
    }

    // Parse note blocks
    if (trimmedLine.startsWith('!note ')) {
      blocks.push({
        type: 'note',
        content: trimmedLine.substring(6).trim()
      })
      continue
    }

    // Parse warning blocks
    if (trimmedLine.startsWith('!warning ')) {
      blocks.push({
        type: 'callout',
        content: {
          text: trimmedLine.substring(9).trim(),
          variant: 'warning'
        }
      })
      continue
    }

    // Parse important blocks
    if (trimmedLine.startsWith('!important ')) {
      blocks.push({
        type: 'callout',
        content: {
          text: trimmedLine.substring(11).trim(),
          variant: 'info'
        }
      })
      continue
    }

    // Parse headers - use RegExp.exec() instead of String.match()
    const headerRegex = /^(#{1,3})\s+(.+)$/
    const headerMatch = headerRegex.exec(trimmedLine)
    if (headerMatch?.[1] && headerMatch?.[2]) {
      const level = headerMatch[1].length
      blocks.push({
        type: `h${level}` as keyof BlockContent,
        content: headerMatch[2].trim()
      })
      continue
    }

    // Parse list items - use RegExp.exec() instead of String.match()
    const listRegex = /^(\d+)\.\s+(.+)$/
    const listMatch = listRegex.exec(trimmedLine)
    if (listMatch?.[1] && listMatch?.[2]) {
      blocks.push({
        type: 'listItem',
        content: {
          number: parseInt(listMatch[1], 10),
          content: listMatch[2].trim()
        }
      })
      continue
    }

    // Default to text block with inline formatting
    blocks.push({
      type: 'text',
      content: trimmedLine
    })
  }

  return blocks
}